import { PdfLayout } from '../PdfLayout';
import { Cpu, Wifi, Usb, MonitorUp, Cable, HeartPulse } from 'lucide-react';

const benefits = [
  {
    icon: Cpu,
    title: 'Industrial Compute Core',
    text: 'Quad-core industrial compute module with 8 GB RAM and on-board eMMC storage, in a fanless DIN-rail enclosure.',
  },
  {
    icon: Cable,
    title: 'Built-In I/O',
    text: '2 isolated digital inputs with millisecond part counting, 2 analog inputs, and a relay output — each one an ordinary tag.',
  },
  {
    icon: Usb,
    title: 'Serial & USB',
    text: '1 RS-232 and 2 RS-485 ports for Modbus RTU, scanners, and scales, plus USB for adapters and cameras.',
  },
  {
    icon: MonitorUp,
    title: 'HDMI & Ethernet',
    text: 'HDMI output for a kiosk screen at the line. Gigabit Ethernet for your factory network.',
  },
  {
    icon: Wifi,
    title: 'Wi-Fi & Optional 4G',
    text: 'Dual-band Wi-Fi, plus an optional 4G cellular modem for remote sites without LAN access.',
  },
  {
    icon: HeartPulse,
    title: 'Looks After Itself',
    text: 'CPU, memory, disk and board temperature published as tags you can alarm on, time kept from your own NTP servers, and scheduled backups to USB or a network share.',
  },
];

export function Appliance() {
  return (
    <PdfLayout
      category="Hardware"
      headline="One Appliance,"
      headlineHighlight="Everything Included"
      description="Scada.Ink ships as a preconfigured appliance — software installed, I/O on board, network ready. Plug it in and configure everything from any web browser. There is nothing to install and no server to maintain."
      accentColor="#3b82f6"
      benefits={benefits}
    >
      <ApplianceMockup />
    </PdfLayout>
  );
}

function ApplianceMockup() {
  return (
    <div className="w-full max-w-[280px]">
      {/* Device illustration */}
      <div className="glass-panel p-4">
        <svg viewBox="0 0 260 200" className="w-full">
          {/* Device body */}
          <rect x="30" y="30" width="200" height="120" rx="10" fill="#1e293b" />
          <rect x="30" y="30" width="200" height="120" rx="10" fill="url(#deviceGrad)" />

          {/* Front panel details */}
          <rect x="42" y="42" width="80" height="50" rx="4" fill="#334155" />
          <rect x="46" y="46" width="72" height="42" rx="2" fill="#0f172a" />
          {/* Screen content */}
          <text x="82" y="62" textAnchor="middle" fontSize="8" fill="#3b82f6" fontWeight="600">Scada.Ink</text>
          <text x="82" y="74" textAnchor="middle" fontSize="6" fill="#64748b">192.168.1.100</text>
          <circle cx="82" cy="82" r="2" fill="#16a34a" />

          {/* Status LEDs */}
          <circle cx="140" y="52" r="3" fill="#16a34a" />
          <circle cx="152" y="52" r="3" fill="#3b82f6" />
          <circle cx="164" y="52" r="3" fill="#64748b" />
          <text x="140" y="46" fontSize="4" fill="#94a3b8">PWR</text>
          <text x="152" y="46" fontSize="4" fill="#94a3b8">NET</text>
          <text x="164" y="46" fontSize="4" fill="#94a3b8">ACT</text>

          {/* Ventilation slots */}
          {[0, 1, 2, 3, 4].map((i) => (
            <rect key={i} x={190} y={45 + i * 8} width="30" height="3" rx="1" fill="#334155" />
          ))}

          {/* Bottom ports row */}
          <rect x="42" y="100" width="176" height="40" rx="4" fill="#0f172a" />

          {/* Port labels and connectors */}
          {/* DI */}
          <rect x="48" y="108" width="22" height="14" rx="2" fill="#334155" stroke="#475569" strokeWidth="0.5" />
          <text x="59" y="118" textAnchor="middle" fontSize="4" fill="#06b6d4">DI×2</text>
          <text x="59" y="130" textAnchor="middle" fontSize="3.5" fill="#64748b">Digital In</text>

          {/* DO */}
          <rect x="76" y="108" width="22" height="14" rx="2" fill="#334155" stroke="#475569" strokeWidth="0.5" />
          <text x="87" y="118" textAnchor="middle" fontSize="4" fill="#16a34a">RLY</text>
          <text x="87" y="130" textAnchor="middle" fontSize="3.5" fill="#64748b">Relay Out</text>

          {/* AI */}
          <rect x="104" y="108" width="22" height="14" rx="2" fill="#334155" stroke="#475569" strokeWidth="0.5" />
          <text x="115" y="118" textAnchor="middle" fontSize="4" fill="#ca8a04">AI×2</text>
          <text x="115" y="130" textAnchor="middle" fontSize="3.5" fill="#64748b">Analog In</text>

          {/* RS-232 */}
          <rect x="132" y="108" width="22" height="14" rx="2" fill="#334155" stroke="#475569" strokeWidth="0.5" />
          <text x="143" y="118" textAnchor="middle" fontSize="4" fill="#7c3aed">232</text>
          <text x="143" y="130" textAnchor="middle" fontSize="3.5" fill="#64748b">RS-232</text>

          {/* RS-485 */}
          <rect x="160" y="108" width="18" height="14" rx="2" fill="#334155" stroke="#475569" strokeWidth="0.5" />
          <text x="169" y="118" textAnchor="middle" fontSize="4" fill="#dc2626">485×2</text>
          <text x="169" y="130" textAnchor="middle" fontSize="3.5" fill="#64748b">RS-485</text>

          {/* ETH */}
          <rect x="184" y="108" width="14" height="14" rx="2" fill="#334155" stroke="#475569" strokeWidth="0.5" />
          <text x="191" y="118" textAnchor="middle" fontSize="4" fill="#3b82f6">ETH</text>
          <text x="191" y="130" textAnchor="middle" fontSize="3.5" fill="#64748b">GbE</text>

          {/* USB + HDMI */}
          <rect x="204" y="108" width="10" height="14" rx="2" fill="#334155" stroke="#475569" strokeWidth="0.5" />
          <text x="209" y="118" textAnchor="middle" fontSize="3.5" fill="#94a3b8">USB</text>

          {/* WiFi antenna icon */}
          <circle cx="205" y="52" r="6" fill="none" stroke="#3b82f6" strokeWidth="0.5" opacity="0.4" />
          <circle cx="205" y="52" r="10" fill="none" stroke="#3b82f6" strokeWidth="0.5" opacity="0.25" />
          <circle cx="205" y="52" r="2" fill="#3b82f6" opacity="0.5" />

          <defs>
            <linearGradient id="deviceGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#334155" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#1e293b" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* DIN rail mount */}
          <rect x="60" y="155" width="140" height="8" rx="2" fill="#94a3b8" opacity="0.3" />
          <text x="130" y="175" textAnchor="middle" fontSize="6" fill="#94a3b8">DIN-Rail Mount · Fanless</text>
        </svg>
      </div>

      {/* I/O summary badges */}
      <div className="flex flex-wrap gap-1 mt-2 justify-center">
        {[
          { label: '2× DI', color: '#06b6d4' },
          { label: '1× Relay', color: '#16a34a' },
          { label: '2× AI', color: '#ca8a04' },
          { label: '1× RS-232', color: '#7c3aed' },
          { label: '2× RS-485', color: '#dc2626' },
          { label: 'USB', color: '#64748b' },
          { label: 'HDMI', color: '#64748b' },
          { label: 'GbE', color: '#3b82f6' },
          { label: 'Wi-Fi', color: '#3b82f6' },
          { label: '4G (Optional)', color: '#7c3aed' },
        ].map((p) => (
          <span
            key={p.label}
            className="text-[6px] font-medium px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: `${p.color}12`, color: p.color }}
          >
            {p.label}
          </span>
        ))}
      </div>
    </div>
  );
}
